import React, {
  DragEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { Link, useLocation } from "react-router-dom";
import Image from "react-bootstrap/Image";
import Container from "react-bootstrap/Container";
import {
  ReferrerInfo,
  ChallengeInvitation,
} from "@nestcoinco/hyper-api-gateway-api-client";

import "./DownloadPage.scss";
import games from "../../contents/games";
import whyPlay from "../../contents/why-play";
import ChooseGameImg from "../../images/choose-game.png";
import ImageOneVsOneResult from "../../images/one-vs-one-result.png";
import ImageXIcon from "../../images/x.svg";
import ImageInstagram from "../../images/instagram-colored.svg";
import ImageTiktok from "../../images/tiktok.svg";
import ImageLinkedIn from "../../images/linkedin.svg";
import HeroImg from "../../images/iphone.png";
import CloseIcon from "../../images/close-icon-purple.svg";
import PlayerOneAvatarImg from "../../images/Avatar-2.svg";
import PlayerTwoAvatarImg from "../../images/Avatar-3.svg";
import HyperCoinImg from "../../images/hyper-coin.svg";
import AndroidImg from "../../images/android-icon.svg";
import PalmPlayImg from "../../images/palmplay-icon.png";
import ImageAppleLogo from "../../images/apple-logo-white.svg";
import PhoneIconImg from "../../images/phone-icon.svg";
import ArrowLeftIcon from "../../images/arrow-left-icon.svg";
import KongClimbSignup from "../../images/games/kong-climb-signup.png";
import { handleGetReferrerInfo } from "../../api-clients/referralsClient";
import { renderHeaders } from "../../utils/renderHeaders";
import { setStoredReferrerInfo } from "../../utils/storage";
import CircularButton from "../../components/Buttons/CircularButton/CircularButton";
import {
  LINKS,
  MVM_EMAIL,
  LandingPageVariants,
  REFERRER_QUERY_NAME,
} from "../../utils/constants";
import { handleGetChallengeInvitationDetails } from "../../api-clients/challengeInvitationsClient";
import { useDownloadApp } from "../../hooks/useDownloadApp";
import { Stack } from "react-bootstrap";
import faqs from "../../contents/faqs";
import AliceCarousel from "react-alice-carousel";
import "react-alice-carousel/lib/alice-carousel.css";
import CollapsibleList from "../../components/CollapsibleList/CollapsibleList";
import FloatingQr from "../../components/FloatingQr/FloatingQr";
import QrCode from "../../components/QrCode/QrCode";
import { Options } from "qr-code-styling";
import AppDownloadButton from "../../components/Buttons/AppDownloadButton/AppDownloadButton";
import DownloadAndroidAppModal from "../../components/DownloadAndroidAppModal/DownloadAndroidAppModal";
import useDisclosure from "../../hooks/useDisclosure";
import ImageCoinStack from "../../images/coin-stack.png";
import useChallengeTimer from "../../hooks/useChallengeTimer";
import useScrollToTopOnMount from "../../hooks/useScrollToTopOnMount";
import ImageAmazonLogo from "../../images/amazon-logo.svg";
import ImageHyperDefaultLogo from "../../images/hyper-full-white.svg";
import ImageHyperArcadeLogo from "../../images/hyper-arcade-logo.svg";
import ImageHyperGamesLogo from "../../images/hyper-games-logo.svg";
import ImageHyperGamingAppLogo from "../../images/hyper-gaming-app-logo.svg";

interface LandingPageSectionProps {
  logo: string;
}

interface AndroidDownloadProps {
  onAndroidDownload?: () => void;
}

enum DownloadOptions {
  ANDROID = "Android",
  TRANSSION = "Transsion",
  APPLE = "Apple",
  AMAZON = "Amazon",
}

const downloadOptions = [
  {
    name: DownloadOptions.APPLE,
    image: ImageAppleLogo,
    url: LINKS.WEBPAGE.APP_STORE_HYPER_PAGE,
  },
  {
    name: DownloadOptions.ANDROID,
    image: AndroidImg,
  },
  {
    name: DownloadOptions.TRANSSION,
    image: PalmPlayImg,
    url: LINKS.WEBPAGE.PALM_STORE_HYPER_PAGE,
  },
  {
    name: DownloadOptions.AMAZON,
    image: ImageAmazonLogo,
    url: LINKS.WEBPAGE.AMAZON_STORE_HYPER_PAGE,
  },
];

const hyperLogos: Record<LandingPageVariants, string> = {
  [LandingPageVariants.DEFAULT]: ImageHyperDefaultLogo,
  [LandingPageVariants.ARCADE]: ImageHyperArcadeLogo,
  [LandingPageVariants.GAMES]: ImageHyperGamesLogo,
  [LandingPageVariants.GAMING_APP]: ImageHyperGamingAppLogo,
};

function DownloadPageTemplate({ variant }: { variant: LandingPageVariants }) {
  useDownloadApp(); // prefetch apk for use later on
  useScrollToTopOnMount();
  const {
    isOpen: isAndroidDownloadOpen,
    onOpen: openAndroidDownload,
    onClose: closeAndroidDownload,
  } = useDisclosure();

  return (
    <>
      {renderHeaders({
        title: "Battle Friends in Fun Games & Make Money | Hyper Gaming App",
        description:
          "Battle friends & frenemies in fun games like candy crush, temple run, and many more. Win your friends, to win the prize pool. Download Hyper Android APK to get started",
      })}
      <Container
        fluid
        className="position-relative px-2 px-lg-0 LandingPageTemplate"
      >
        <HeroSection
          logo={hyperLogos[variant]}
          onAndroidDownload={openAndroidDownload}
        />
        <Row className="body-content d-flex flex-column">
          <WhyPlayGamesSection />
          <ExcitingGamesSection />
          <HowToPlayAndWinSection />
          <Faqs />
        </Row>
        <JoinWhatsAppCommunitySection logo={hyperLogos[variant]} />
        <DesktopFooter logo={hyperLogos[variant]} />
        <Footer onAndroidDownload={openAndroidDownload} />
      </Container>
      <DownloadAndroidAppModal
        isOpen={isAndroidDownloadOpen}
        onClose={closeAndroidDownload}
      />
    </>
  );
}

enum LinkFlow {
  DEFAULT = "default",
  REFERRAL = "referral",
  CHALLENGE = "challenge",
}

function HeroSection({
  logo,
  onAndroidDownload,
}: LandingPageSectionProps & AndroidDownloadProps) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [referrerInfo, setReferrerInfo] = useState<ReferrerInfo | null>(null);
  const [challengeDetails, setChallengeDetails] =
    useState<ChallengeInvitation>();
  const [flow, setFlow] = useState<LinkFlow>(LinkFlow.DEFAULT);
  const mainCopyRef = useRef("");
  const referrerId = queryParams.get(REFERRER_QUERY_NAME);
  const challengeId = queryParams.get("challengeId");
  const customQrOptions: Options = {
    width: 95,
    height: 95,
    type: "canvas",
    image: "",
    backgroundOptions: {
      color: "",
    },
  };
  const timeLeft = useChallengeTimer(challengeId || "");

  switch (flow) {
    case LinkFlow.CHALLENGE:
      mainCopyRef.current = `${
        challengeDetails?.inviter.username || ""
      } wants to play with you`;
      break;
    case LinkFlow.REFERRAL:
      mainCopyRef.current = "Play fun games, compete & win cash";
      break;
    default:
      mainCopyRef.current = "Play fun games, compete & win";
      break;
  }

  useEffect(() => {
    if (!referrerId && !challengeId) {
      setFlow(LinkFlow.DEFAULT);
    } else if (referrerId && !challengeId) {
      setFlow(LinkFlow.REFERRAL);
      return;
    } else if (referrerId && challengeId) {
      setFlow(LinkFlow.CHALLENGE);
    }
  }, [challengeId, referrerId]);

  useEffect(() => {
    if (!referrerId || referrerId.toLowerCase().startsWith("af_")) return;
    const getReferrerInfo = async () => {
      const info = await handleGetReferrerInfo(referrerId);
      if (!info) return;
      setReferrerInfo(info);
      setStoredReferrerInfo(info);
    };

    getReferrerInfo();
  }, [referrerId]);

  useEffect(() => {
    if (!challengeId) return;
    const getChallengeDetails = async () => {
      const details = await handleGetChallengeInvitationDetails(challengeId);
      if (!details) return;
      setChallengeDetails(details);
    };
    getChallengeDetails();
  }, [challengeId]);

  return (
    <>
      <Col className="App-bar d-flex justify-content-center position-fixed">
        <Link to="/">
          <Image className="img" src={logo}></Image>
        </Link>
      </Col>
      <Row
        as="header"
        className="text-white text-center text-md-start d-flex flex-column position-relative pb-5 Header"
      >
        <Col className="pb-3 isolate pt-4 pt-lg-0 d-flex flex-column flex-lg-row align-items-center">
          <Col
            as="h1"
            className="
              heading text-center text-lg-start flex-grow-0 fs-1 px-0 mb-0 pt-5 pt-lg-1 pb-1 hero-text
            "
          >
            <span className="font-lg mb-0 d-lg-none">
              {mainCopyRef.current}
            </span>
            <Stack className="d-none d-lg-flex desktop-hero-text">
              <Stack className="copy-text">
                <p className="mb-0 main-text">
                  Play fun games, compete &
                  <br className="d-none d-lg-inline" /> win{" "}
                  <span className="money-bag">💰💰💰</span>
                </p>
                <p className="fs-4 fw-normal mb-0 sub-text">
                  One app with 100s of the best games from the App Store
                </p>
              </Stack>
              <Stack
                direction="horizontal"
                className="align-items-end gap-4"
                style={{ width: "max-content" }}
              >
                <Stack className="align-items-center gap-0 p-2 bg-white rounded-4">
                  <Stack
                    direction="horizontal"
                    className="gap-1 p-1 scan-heading"
                  >
                    <Image
                      src={PhoneIconImg}
                      alt="Phone icon"
                      width={16}
                      height={16}
                    />
                    <span className="fw-semibold">Scan QR</span>
                  </Stack>
                  <QrCode
                    value="https://hypergame.onelink.me/4tib/dw8iofia"
                    className="barcode"
                    customOptions={customQrOptions}
                  />
                </Stack>
                <Stack className="justify-content-end download-cta">
                  <p className="fw-semibold mb-0 download-text">
                    Hyper is available on
                  </p>
                  <Stack direction="horizontal" className="download-links">
                    {downloadOptions.map((option) => {
                      const icon = (
                        <Image
                          src={option.image}
                          alt={option.name}
                          width={30}
                          height={30}
                        />
                      );

                      if (option.name === DownloadOptions.ANDROID) {
                        return (
                          <button
                            key={option.name}
                            type="button"
                            className="download-link"
                            onClick={onAndroidDownload}
                          >
                            {icon}
                          </button>
                        );
                      }

                      return (
                        <a
                          key={option.name}
                          href={option.url}
                          target="_blank"
                          rel="noreferrer"
                          className="download-link"
                        >
                          {icon}
                        </a>
                      );
                    })}
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
            {flow === LinkFlow.DEFAULT && (
              <span className="d-lg-none">
                <br />
                💰💰💰
              </span>
            )}
            {flow === LinkFlow.CHALLENGE && (
              <div
                className="mt-3 d-lg-none"
                style={{
                  marginBottom: challengeDetails?.rewardAmount ? 0 : 24,
                }}
              >
                <div className="day-timer-container">
                  <p className="you-have-text">Play before</p>
                  <p className="day-timer">
                    {timeLeft.split(":").map((v, i) => (
                      <>
                        <span className="time-part">{v}</span>
                        {i !== 2 && <span className="colon px-1">:</span>}
                      </>
                    ))}
                  </p>
                </div>
              </div>
            )}
          </Col>
          {referrerInfo && flow === LinkFlow.REFERRAL && (
            <p className="referrer-text p-0 my-3 d-lg-none">
              @{referrerInfo.username} is gifting
              <span className="bonus-fire-text ms-1">100% bonus 🔥</span>on 1st
              deposit
            </p>
          )}
          {flow === LinkFlow.CHALLENGE && !!challengeDetails?.rewardAmount && (
            <div className="referrer-text my-0 d-flex align-items-center flex-column my-3 d-lg-none">
              <p className="winner-gets-text">Winner gets</p>
              <p className="hpr-winnings">
                {" "}
                <Image
                  src={ImageCoinStack}
                  height={17}
                  width={25}
                  className="ms-1 me-1"
                />
                {challengeDetails?.rewardAmount} HPR
              </p>
            </div>
          )}
          {flow !== LinkFlow.CHALLENGE && (
            <>
              <Image className="mt-3 hero-image d-lg-none" src={HeroImg} />
            </>
          )}
          {flow === LinkFlow.CHALLENGE && (
            <div className="challenge-img-container mt-2 d-lg-none">
              <div className="challenge-info-container">
                <p className="d-flex justify-content-end m-1">
                  <CircularButton
                    color="transparent"
                    image={CloseIcon}
                    buttonClass="close-btn mb-1 p-0"
                    disabled
                  />
                </p>
                <h4 className="challenge-heading mt-1">
                  Challenge {challengeDetails?.inviter.username || ""}
                </h4>

                <div className="player-imgs-box">
                  <div className="player-one-img-box  rounded-circle d-flex justify-content-center  align-align-items-center ">
                    <Image
                      src={PlayerOneAvatarImg}
                      className="player-avatar d-block "
                      width={24}
                      height={25}
                    />
                  </div>
                  <div className="player-two-img-box rounded-circle ">
                    <Image
                      src={
                        challengeDetails?.inviter.avatarUrl ||
                        PlayerTwoAvatarImg
                      }
                      className="player-avatar d-block "
                      width={24}
                      height={25}
                    />
                  </div>
                </div>

                <div className="text-center">
                  <Image
                    src={
                      challengeDetails?.games &&
                      challengeDetails.games[0].thumbnail
                    }
                    className="game-logo"
                  />
                  <span className="game-text">
                    {challengeDetails?.games
                      ? challengeDetails.games[0].name
                      : ""}
                  </span>
                </div>

                {!!challengeDetails?.rewardAmount && (
                  <>
                    <p className="winner-gets-text text-center ">Winner gets</p>

                    <p className="bounty-text text-center ">
                      <Image src={HyperCoinImg} className="hypercoin-img" />{" "}
                      {challengeDetails.rewardAmount}
                    </p>
                  </>
                )}
                <span className="challenge-with-btn">
                  {!!challengeDetails?.stakeAmount ? (
                    <>
                      <span className="challenge-with-text">
                        Challenge with
                      </span>
                      <Image src={HyperCoinImg} height={8} width={8} />
                      <span className="challenge-with-text">
                        {challengeDetails?.stakeAmount}
                      </span>
                    </>
                  ) : (
                    <span className="challenge-with-text">Challenge</span>
                  )}
                </span>
              </div>
            </div>
          )}
        </Col>
        <FloatingQr />
      </Row>
    </>
  );
}

function WhyPlayGamesSection() {
  return (
    <Col className="mt-15 page-section WhyPlaySection">
      <h2 className="gradient-text pb-0 why-play-title">
        Why play games <br className="d-none d-md-inline" /> on Hyper?
      </h2>
      <Col className="d-flex justify-content-between why-play-grid">
        {whyPlay.map((item, i) => (
          <div
            key={item.key}
            className="d-flex flex-column align-items-center why-play"
          >
            <img src={item.image} alt="" />
            <p className="text-center py-1 mt-md-3 mb-0 colored">{item.copy}</p>
            <p className="text-center py-1 text-white mb-0">{item.subCopy}</p>
          </div>
        ))}
      </Col>
    </Col>
  );
}

const handleDragStart: DragEventHandler = (e) => e.preventDefault();

const gameItems = games.map((item) => (
  <div
    key={item.key}
    onDragStart={handleDragStart}
    className="col d-flex flex-column justify-content-between align-items-center gap-1 gap-lg-3 mx-lg-4 fancy-border game-item"
  >
    <img src={item.image} alt="" style={{ objectFit: "contain" }} />
    <p className="text-center text-white mb-0 px-1">{item.content}</p>
  </div>
));

const resposiveValues = {
  0: {
    items: 3,
  },
};

enum NavDirection {
  PREVIOUS = "prev",
  NEXT = "next",
}

function ExcitingGamesSection() {
  const carouselSectionRef = useRef<HTMLDivElement>(null);
  const [padding, setPadding] = useState(0);

  const syncState = () => {
    const percentageForPadding = 0.13;
    const { current } = carouselSectionRef;
    if (current) {
      setPadding(current.offsetWidth * percentageForPadding);
    }
  };

  useEffect(syncState, []);

  const renderNavButtons = useCallback((dir: NavDirection) => {
    return (
      <button className="carousel-nav-button">
        <Image
          src={ArrowLeftIcon}
          alt="Next button"
          style={{
            rotate: dir === NavDirection.NEXT ? "180deg" : "0deg",
          }}
        />
      </button>
    );
  }, []);

  return (
    <Col className="px-3 d-flex flex-column ExcitingGames">
      <h2 className="gradient-text">
        Play thrilling Games
        <br className="d-none d-md-inline" /> with friends on Hyper
      </h2>
      <div ref={carouselSectionRef} className="game-carousel">
        <AliceCarousel
          items={gameItems}
          responsive={resposiveValues}
          mouseTracking
          autoPlay
          autoPlayStrategy="none"
          autoPlayInterval={1000}
          animationDuration={1000}
          animationType="fadeout"
          infinite
          paddingRight={padding}
          onResized={syncState}
          disableDotsControls
          disableButtonsControls
          renderPrevButton={() => renderNavButtons(NavDirection.PREVIOUS)}
          renderNextButton={() => renderNavButtons(NavDirection.NEXT)}
        />
      </div>
    </Col>
  );
}

function HowToPlayAndWinSection() {
  return (
    <Col className="d-flex flex-column page-section HowToPlayAndWin">
      <h2 className="py-0 gradient-text" style={{ letterSpacing: -0.17 }}>
        How to Play Games And
        <br className="d-none d-lg-inline" /> Win
        <span className="dollar-icon"> 💰💰💰</span>
      </h2>
      <Col className="d-flex flex-wrap flex-lg-nowrap justify-content-lg-between InfoItems">
        <Col className="green text-white d-flex flex-column align-items-center justify-content-between pt-4 mb-3 m-lg-0 how-to-container">
          <div className="d-flex flex-column align-items-center how-to-item">
            <h3 className="text-white item-count">1</h3>
            <h6 className="item-title">Download Hyper & Sign up</h6>
            <p className="item-copy">
              Tap download & install the app from your phone's download folder
              to sign up.
            </p>
          </div>
          <Image src={KongClimbSignup} alt="Kong Climb game demo" />
        </Col>
        <Col className="blue text-white d-flex flex-column align-items-center justify-content-between pt-4 my-3 m-lg-0 how-to-container">
          <div className="d-flex flex-column align-items-center how-to-item">
            <h3 className="text-white item-count">2</h3>
            <h6 className="item-title">Choose a Game</h6>
            <p className="item-copy">
              Play for free or play for cash in multi-player tournaments &
              head-to-head contests.
            </p>
          </div>
          <Image src={ChooseGameImg} alt="Hyper dashboard" />
        </Col>
        <Col className="orange text-white d-flex flex-column align-items-center justify-content-between pt-4 my-3 m-lg-0 how-to-container">
          <div className="d-flex flex-column align-items-center how-to-item">
            <h3 className="text-white item-count">3</h3>
            <h6 id="canvas" className="item-title">
              Beat Your Opponent & Win Cash
            </h6>
            <p className="item-copy">
              Beat your opponent, level up & win the 💰 prize.
            </p>
          </div>
          <Image src={ImageOneVsOneResult} alt="1v1 game result" />
        </Col>
      </Col>
    </Col>
  );
}

function Faqs() {
  return (
    <div className="d-flex flex-wrap justify-content-lg-between gap-5 text-start page-section Faqs">
      <Stack gap={2} className="copy-section">
        <p className="fw-bold mb-0 copy">
          Some things
          <br /> you may want
          <br className="d-none d-lg-inline" /> to know
        </p>
        <p className="sub-copy">
          We&apos;ve answered some questions asked by the <br /> community.
          Couldn&apos;t find an answer to your question? <br /> Feel free to{" "}
          <a href={`mailto:${MVM_EMAIL}`}>email us!</a>
        </p>
      </Stack>

      <CollapsibleList items={faqs} className="questions" />
    </div>
  );
}

function JoinWhatsAppCommunitySection({ logo }: LandingPageSectionProps) {
  return (
    <Row className="foot d-flex flex-column d-lg-none JoinWhatsAppCommunity">
      <Col className="logo-container">
        <Image src={logo} width={100} />
      </Col>
      <Col className="d-flex justify-content-center pb-3">
        <Row className="d-flex justify-content-between w-100">
          <Col className="d-flex justify-content-start align-items-end w-100 p-0 gap-3">
            <a
              href={LINKS.SOCIAL.INSTAGRAM}
              target="_blank"
              rel="noreferrer"
              aria-label="instagram"
            >
              <Image src={ImageInstagram} alt="instagram icon" />
            </a>
            <a
              href={LINKS.SOCIAL.X}
              target="_blank"
              rel="noreferrer"
              aria-label="x"
            >
              <Image src={ImageXIcon} alt="x icon" />
            </a>
            <a
              href={LINKS.SOCIAL.TIKTOK}
              target="_blank"
              rel="noreferrer"
              aria-label="Tik Tok"
            >
              <Image src={ImageTiktok} alt="tiktok icon" />
            </a>
            <a
              href={LINKS.SOCIAL.LINKEDIN}
              target="_blank"
              rel="noreferrer"
              aria-label="linkedin"
            >
              <Image src={ImageLinkedIn} alt="linkedin icon" />
            </a>
          </Col>
          <Col className="d-flex justify-content-end align-items-end w-100 p-0 gap-2">
            <a
              href={LINKS.WEBPAGE.PRIVACY_POLICY}
              target="_blank"
              rel="noreferrer"
              className="link-text"
            >
              Privacy Policy
            </a>
            <a
              href={LINKS.WEBPAGE.TERMS_AND_CONDITIONS}
              target="_blank"
              rel="noreferrer"
              className="link-text"
            >
              Terms of service
            </a>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}

function DesktopFooter({ logo }: LandingPageSectionProps) {
  return (
    <Col className="d-none d-lg-flex flex-row justify-content-between align-items-center DesktopFooter">
      <Stack gap={3} className="contact-details">
        <a href={`mailto:${MVM_EMAIL}`} className="email">
          {MVM_EMAIL}
        </a>
        <Stack direction="horizontal" gap={3} className="social-links">
          <a
            href={LINKS.SOCIAL.INSTAGRAM}
            target="_blank"
            rel="noreferrer"
            className="text-decoration-none"
          >
            <Image
              src={ImageInstagram}
              alt="Instagram Logo"
              width={24}
              height={24}
            />
          </a>
          <a
            href={LINKS.SOCIAL.X}
            target="_blank"
            rel="noreferrer"
            className="text-decoration-none"
          >
            <Image
              src={ImageXIcon}
              alt="X/Twitter Logo"
              width={24}
              height={24}
            />
          </a>
          <a
            href={LINKS.SOCIAL.TIKTOK}
            target="_blank"
            rel="noreferrer"
            className="text-decoration-none"
          >
            <Image src={ImageTiktok} alt="TikTok Logo" width={24} height={24} />
          </a>
          <a
            href={LINKS.SOCIAL.LINKEDIN}
            target="_blank"
            rel="noreferrer"
            className="text-decoration-none"
          >
            <Image
              src={ImageLinkedIn}
              alt="LinkedIn Logo"
              width={24}
              height={24}
            />
          </a>
        </Stack>
      </Stack>
      <Col className="logo-container">
        <Image src={logo} width={178} height={30} />
      </Col>
      <Stack gap={3} className="terms-and-privacy">
        <a
          href={LINKS.WEBPAGE.TERMS_AND_CONDITIONS}
          target="_blank"
          rel="noreferrer"
          className="fs-6"
        >
          Terms of service
        </a>
        <a
          href={LINKS.WEBPAGE.PRIVACY_POLICY}
          target="_blank"
          rel="noreferrer"
          className="fs-6"
        >
          Privacy policy
        </a>
      </Stack>
    </Col>
  );
}

function Footer({ onAndroidDownload }: AndroidDownloadProps) {
  return (
    <Col className="App-footer d-flex justify-content-center px-3 py-3 position-fixed d-md-none MobileOnly">
      <AppDownloadButton onAndroidDownload={onAndroidDownload} />
    </Col>
  );
}

export default DownloadPageTemplate;
